var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// src/components/Hotspot.tsx
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { useHotspot } from '../context/HotspotContext';
import { useStream } from '../../interlucent/StreamContext';
import { isMobile } from 'react-device-detect';
import { createPortal } from 'react-dom';
export default function Hotspot(props) {
    var _a, _b, _c;
    var hotspots = useHotspot().hotspots;
    var currentHotspot = hotspots.find(function (h) { return h.name === props.name; });
    var videoOffset = useStream().videoOffset;
    var _d = useState({ left: '50%', top: '50%' }), position = _d[0], setPosition = _d[1];
    var _e = useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var _f = useState(false), mobileVisible = _f[0], setMobileVisible = _f[1];
    var _g = useState(false), isVisible = _g[0], setIsVisible = _g[1];
    var _h = useState(false), animateLines = _h[0], setAnimateLines = _h[1];
    var _j = useState(false), isLeft = _j[0], setIsLeft = _j[1];
    var wrapperRef = useRef(null);
    var popupRef = useRef(null);
    var verticalLength = 60;
    var diagonalLength = 70;
    // Reflète exactement `width: clamp(350px, 32vw, 500px)` de .hotspotPopup (voir
    // Hotspot.css). Calculé ici plutôt que lu via popupRef.current?.offsetWidth :
    // cette dernière vaut 0/null tant que la popup n'a pas encore été mesurée au
    // moins une fois, ce qui ne concerne QUE le cas isLeft (popupLeft ci-dessous
    // est le seul des deux calculs à dépendre de la largeur) — la toute première
    // ouverture à gauche pouvait donc placer la carte au mauvais endroit (chevauchant
    // le point) pendant une frame, avant de se corriger.
    var estimatedPopupWidth = Math.min(500, Math.max(350, window.innerWidth * 0.32));
    var diagonalAngle = isLeft ? -30 : 30;
    var diagonalOffsetX = Math.cos((Math.PI / 180) * diagonalAngle) * diagonalLength;
    var diagonalOffsetY = Math.sin((Math.PI / 180) * Math.abs(diagonalAngle)) * diagonalLength;
    var popupLeft = isLeft
        ? "calc(50% - ".concat(Math.abs(diagonalOffsetX), "px - ").concat(((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || estimatedPopupWidth, "px)")
        : "calc(50% + ".concat(Math.abs(diagonalOffsetX), "px)");
    var popupTop = "calc(-".concat(verticalLength, "px - ").concat(diagonalOffsetY / 2, "px)");
    useEffect(function () {
        if (currentHotspot && currentHotspot.screenX !== 0 && currentHotspot.screenY !== 0) {
            var proportionX = currentHotspot.XPosition / currentHotspot.screenX;
            var proportionY = currentHotspot.YPosition / currentHotspot.screenY;
            // La vidéo peut être en letterbox/pillarbox dans son conteneur (aspect ratio
            // du stream Unity vs. celui de la fenêtre) : videoOffset donne le rectangle
            // pixel réellement occupé par la vidéo. Sans ça, le hotspot est positionné
            // en % du conteneur entier, décalé par rapport à ce qui est visuellement
            // affiché dès qu'il y a des bandes noires.
            var hasVideoRect = videoOffset.width > 0 && videoOffset.height > 0;
            var dotXpx = hasVideoRect
                ? videoOffset.x + proportionX * videoOffset.width
                : proportionX * window.innerWidth;
            var dotYpx = hasVideoRect
                ? videoOffset.y + (1 - proportionY) * videoOffset.height
                : (1 - proportionY) * window.innerHeight;
            setPosition({
                left: "".concat(dotXpx, "px"),
                top: "".concat(dotYpx, "px"),
            });
        }
    }, [currentHotspot, videoOffset]);
    // Orientation de la popup (gauche/droite) : calculée uniquement à l'ouverture,
    // pas à chaque mise à jour de position. Unity envoie la position du hotspot en
    // continu tant que la caméra bouge, même popup déjà ouverte — recalculer isLeft
    // à chaque fois pouvait le faire basculer en plein milieu de l'animation de la
    // ligne diagonale. Sa largeur a une transition CSS douce, mais pas sa rotation
    // (transform), donc l'angle changeait alors instantanément, à mi-chemin du
    // tracé : un bout de la ligne semblait partir dans le mauvais sens.
    useEffect(function () {
        var _a;
        if (isOpen && currentHotspot && currentHotspot.screenX !== 0 && currentHotspot.screenY !== 0) {
            var proportionX = currentHotspot.XPosition / currentHotspot.screenX;
            var hasVideoRect = videoOffset.width > 0 && videoOffset.height > 0;
            var dotXpx = hasVideoRect
                ? videoOffset.x + proportionX * videoOffset.width
                : proportionX * window.innerWidth;
            var popupWidth = ((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || estimatedPopupWidth;
            var spaceRight = window.innerWidth - dotXpx;
            setIsLeft(spaceRight < popupWidth + diagonalLength);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    // Fermer si clic hors du hotspot/popup
    useEffect(function () {
        var handleClickOutside = function (event) {
            if (event.target.closest('.hotspotPopup-mobile'))
                return;
            if (event.target.closest('.hotspotPopup-mobile-overlay'))
                return;
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return function () { return document.removeEventListener('mousedown', handleClickOutside); };
    }, []);
    // Animations d’apparition popup
    useEffect(function () {
        if (isOpen) {
            setAnimateLines(false);
            requestAnimationFrame(function () { return setAnimateLines(true); });
            var popupDelay_1 = setTimeout(function () { return setIsVisible(true); }, 300);
            return function () { return clearTimeout(popupDelay_1); };
        }
        else {
            setIsVisible(false);
            setAnimateLines(false);
        }
    }, [isOpen]);
    // Affichage mobile
    useEffect(function () {
        if (isOpen && isMobile) {
            var t_1 = requestAnimationFrame(function () { return setMobileVisible(true); });
            return function () { return cancelAnimationFrame(t_1); };
        }
        setMobileVisible(false);
    }, [isOpen, isMobile]);
    // --- NOUVEAU : diffusion d’événements globaux pour l’overlay d’aide ---
    useEffect(function () {
        if (isOpen) {
            window.dispatchEvent(new CustomEvent('hotspot:open', { detail: { name: props.name } }));
        }
        else {
            window.dispatchEvent(new CustomEvent('hotspot:close', { detail: { name: props.name } }));
        }
    }, [isOpen, props.name]);
    var togglePopup = function () {
        setIsOpen(function (prev) { return !prev; });
        // Toute interaction hotspot réinitialise le timer de l’overlay
        window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'toggle' } }));
    };
    if (!currentHotspot)
        return null;
    var rootEl = document.getElementById('root');
    if (!rootEl)
        return null;
    return (_jsxs("div", __assign({ className: "hotspotWrapper", ref: wrapperRef, style: {
            position: 'absolute',
            left: position.left,
            top: position.top,
            transform: 'translate(-50%, -50%)',
            zIndex: isOpen ? 6 : 5,
        } }, { children: [_jsx("div", { className: "hotspotDot ".concat(isOpen ? 'active' : ''), role: "button", tabIndex: 0, onClick: togglePopup, onKeyDown: function (e) {
                    if (e.key === 'Enter' || e.key === ' ')
                        togglePopup();
                } }), isOpen &&
                (isMobile
                    ? createPortal(_jsxs(_Fragment, { children: [_jsx("div", { className: "hotspotPopup-mobile-overlay", role: "button", tabIndex: 0, "aria-label": "Fermer la fen\u00EAtre", onClick: function () {
                                    setIsOpen(false);
                                    window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'overlay-close' } }));
                                }, onKeyDown: function (e) {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setIsOpen(false);
                                        window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'overlay-close' } }));
                                    }
                                } }), _jsxs("div", __assign({ className: "hotspotPopup-mobile ".concat(mobileVisible ? 'visible' : ''), role: "dialog", "aria-modal": "true" }, { children: [_jsx("button", __assign({ className: "hotspotClose", "aria-label": "Fermer", onClick: function () {
                                            setIsOpen(false);
                                            window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'close' } }));
                                        } }, { children: "\u00D7" })), _jsx("div", { className: "hotspotPopup-mobile-img", style: { backgroundImage: "url(".concat(props.backgroundImg, ")") } }), _jsxs("div", __assign({ className: "hotspotPopup-mobile-content" }, { children: [_jsx("h3", __assign({ className: "hotspotHeader" }, { children: props.title })), _jsx("p", __assign({ className: "hotspotSub" }, { children: props.subtitle })), _jsx("div", __assign({ className: "hotspotIcons" }, { children: (_b = props.certifications) === null || _b === void 0 ? void 0 : _b.map(function (cert, i) { return (_jsx("img", { src: "imgs/icons/".concat(cert.image, ".svg"), alt: cert.tooltip, className: "hotspotIcon" }, i)); }) })), _jsx(Button, __assign({ className: "hotspotButton ".concat(props.className, " btn-factory"), onClick: function () {
                                                    props.changeScene(props.className);
                                                    props.setIsShown(false);
                                                    setIsOpen(false);
                                                    window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'launch' } }));
                                                } }, { children: props.launchBtn }))] }))] }))] }), rootEl)
                    : (_jsxs(_Fragment, { children: [_jsx("div", { className: "hotspotLine-vertical ".concat(animateLines ? 'visible' : '') }), _jsx("div", { className: "hotspotLine-diagonal ".concat(isLeft ? 'left-orientation' : 'right-orientation', " ").concat(animateLines ? 'visible' : '') }), _jsxs("div", __assign({ ref: popupRef, className: "hotspotPopup ".concat(isVisible ? 'visible' : ''), style: { left: popupLeft, top: popupTop } }, { children: [_jsx("div", { className: "hotspotPopup-image", style: { backgroundImage: "url(".concat(props.backgroundImg, ")") } }), _jsxs("div", __assign({ className: "hotspotPopup-content" }, { children: [_jsxs("div", { children: [_jsx("div", __assign({ className: "hotspotHeader" }, { children: props.title })), _jsx("div", __assign({ className: "hotspotSub" }, { children: props.subtitle }))] }), _jsx("div", __assign({ className: "hotspotIcons" }, { children: (_c = props.certifications) === null || _c === void 0 ? void 0 : _c.map(function (cert, i) { return (_jsx(OverlayTrigger, __assign({ placement: "top", overlay: _jsx(Tooltip, { children: cert.tooltip }) }, { children: _jsx("img", { src: "imgs/icons/".concat(cert.image, ".svg"), alt: cert.tooltip, className: "hotspotIcon" }) }), i)); }) })), _jsx(Button, __assign({ className: "hotspotButton ".concat(props.className, " btn-factory"), onClick: function () {
                                                    props.changeScene(props.className);
                                                    props.setIsShown(false);
                                                    window.dispatchEvent(new CustomEvent('hotspot:interact', { detail: { name: props.name, action: 'launch' } }));
                                                } }, { children: props.launchBtn }))] }))] }))] })))] })));
}
